import { Fragment, useMemo, useState, useCallback } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { DataGrid, KeenIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { selectUserPermissions } from '@/store/authSlice';
import {
  useGetPlatformSettingsQuery,
  useUpdatePlatformSettingMutation,
} from '@/services/PlatformSettingsService';
import { PlatformSetting } from '@/types/platform-settings.types';
import { PaginationState } from '@tanstack/react-table';
import { formatDisplayDateTime } from '@/utils/Date';
import { Loader2 } from 'lucide-react';

const PlatformSettingListing = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const userPermissions = useSelector(selectUserPermissions);

  const hasUpdatePermission = useMemo(() => {
    return userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('platform_setting_update')
    );
  }, [userPermissions]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [confirmedValues, setConfirmedValues] = useState<
    Record<string, string>
  >({});

  const { data: settingsData, isLoading } = useGetPlatformSettingsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: search || undefined,
  });

  const [updateSetting] = useUpdatePlatformSettingMutation();

  const handleToggle = useCallback(
    async (id: string, key: string, currentValue: string) => {
      setTogglingId(id);
      const newValue = currentValue === 'true' ? 'false' : 'true';
      try {
        await updateSetting({ id, data: { key, value: newValue } }).unwrap();
        setConfirmedValues((prev) => ({ ...prev, [id]: newValue }));
        enqueueSnackbar('Setting updated successfully', { variant: 'success' });
      } catch {
        enqueueSnackbar('Failed to update setting', { variant: 'error' });
      } finally {
        setTogglingId(null);
      }
    },
    [updateSetting, enqueueSnackbar]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'key',
        header: 'Setting Key',
        cell: ({ row }: { row: { original: PlatformSetting } }) => (
          <span className="font-medium text-gray-800">{row.original.key}</span>
        ),
      },
      {
        accessorKey: 'value',
        header: 'Value',
        cell: ({ row }: { row: { original: PlatformSetting } }) =>
          row.original.key === 'Logo URL' ? (
            <img
              src={row.original.value}
              alt="Logo"
              className="w-auto h-10 p-1 object-contain bg-[#105B78]"
            />
          ) : (
            <span className="text-gray-700">{row.original.value}</span>
          ),
      },
      {
        accessorKey: 'updated_at',
        header: 'Updated At',
        cell: ({ row }: { row: { original: PlatformSetting } }) => (
          <span className="text-gray-600">
            {row.original.updated_at
              ? formatDisplayDateTime(row.original.updated_at, {
                  hour12: false,
                })
              : '-'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: PlatformSetting } }) => {
          const isToggling = togglingId === row.original.id;
          const displayValue =
            confirmedValues[row.original.id] ?? row.original.value;
          return (
            <div className="flex items-center gap-2 justify-center">
              {row.original.value_type === 'boolean' ? (
                isToggling ? (
                  <Loader2
                    className="animate-spin text-gray-400 text-center"
                    size={16}
                  />
                ) : (
                  <label className="switch switch-sm">
                    <input
                      type="checkbox"
                      checked={displayValue === 'true'}
                      disabled={!hasUpdatePermission}
                      onChange={() =>
                        handleToggle(
                          row.original.id,
                          row.original.key,
                          displayValue
                        )
                      }
                      className="cursor-pointer"
                      aria-label={`Toggle setting ${row.original.key}`}
                    />
                  </label>
                )
              ) : (
                hasUpdatePermission && (
                  <button
                    className="btn btn-sm btn-icon btn-light"
                    onClick={() =>
                      navigate(`/platform-setting/edit/${row.original.id}`)
                    }
                    title="Edit"
                  >
                    <KeenIcon icon="pencil" />
                  </button>
                )
              )}
            </div>
          );
        },
        meta: { className: 'w-20' },
      },
    ],
    [navigate, handleToggle, hasUpdatePermission, togglingId, confirmedValues]
  );

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <h1 className="sr-only">Platform Settings</h1>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Platform Settings" />
              <ToolbarDescription>Manage platform settings</ToolbarDescription>
            </ToolbarHeading>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <div className="relative">
                <KeenIcon
                  icon="magnifier"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by key..."
                  className="input input-sm pl-10 w-64 h-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search settings by key"
                />
              </div>
            </div>
          </div>
          <div className="card-body">
            <DataGrid
              columns={columns}
              data={settingsData?.data || []}
              loading={isLoading}
              serverSide
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems: settingsData?.total || 0,
                totalPages: settingsData?.totalPages || 0,
                setPagination,
              }}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export { PlatformSettingListing };
